import { TamediaStyles } from "@ta-interaktiv/microfrontend-template-helpers";
import styled, { ThemeProvider, keyframes } from 'styled-components'
import { GlobalVariables } from 'Globals/GlobalVariables'
import { getTheme } from '../../theme/config'
import { useEffect, useRef, useState, useMemo, useCallback } from 'react'
import { useGetSize } from 'Hooks/useGetSize'
// @ts-ignore
import { Scrollama, Step } from 'react-scrollama'
import { texts } from './texts'
import { Calendar } from './Calendar'
import { Confettis } from './Confettis'
import { animated, useSpring, config } from '@react-spring/web'
import { throttle } from 'throttle-debounce'
import { ExpandableContent } from './ExpandableContent'
import { PathEventMarker } from './PathEventMarker'
interface Month {
  month: number
  point: DOMPoint | undefined
}

const getDayInYear = (date: Date) => {
  if (!date) return 0
  const start = new Date(2024, 0, 0)
  const diff = (date.getTime() - start.getTime()) / 1000
  const oneDay = 60 * 60 * 24
  return Math.floor(diff / oneDay)
}

const MainComponent = ({
  tenant,
  colorMode,
  properties,
  trackEvent,
}: MicroFrontendProps) => {
  const [state, setState] = useState<{
    step: number
    progress: number
  }>({
    step: 0,
    progress: 0,
  })
  const dates = useMemo(
    () =>
      texts.map((t) => ({
        ...t,
        pathRangeInDays:
          getDayInYear(t.pathRange[1]) - getDayInYear(t.pathRange[0]),
      })),
    []
  )
  // const totalProgress = useMemo(
  //   () =>
  //     state.step / dates.length -
  //     (1 / dates.length) * Math.abs(1 - state.progress),
  //   [state.step, state.progress, dates.length]
  // )

  const theme = getTheme(colorMode, tenant)
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollyTellingContainerRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const { width } = useGetSize(containerRef)
  const height = width * 2
  const pathRef = useRef<SVGPathElement>(null)
  const months = useRef<Month[]>([])
  const days = useRef<Month[]>([])
  const [pathStep, setPathStep] = useState(0)
  const pathStepRef = useRef<number>(0)

  const today = new Date()
  const [pathLength, setPathLength] = useState<number>(0)
  const pathLengthRef = useRef<number>(0)
  const daysCount = 397
  const dayLength = pathLength / daysCount

  const day = Math.round(
    dates
      .slice(0, Math.max(0, state.step - 1))
      .reduce((a, b) => a + b.pathRangeInDays, 0) +
      state.progress * dates[Math.max(0, state.step - 1)].pathRangeInDays
  )

  // const mainPathSpring = useSpring({
  //   strokeDashoffset: pathLength - dayLength * day,
  //   config: config.stiff,
  // })

  const [mainPathSpring, mainPathSpringApi] = useSpring(
    () => ({
      strokeDashoffset: pathLength,
      config: config.stiff,
    }),
    [pathLength]
  )

  // const artBoardOverflowSpring = useSpring({
  //   translateY: chartOverflow * totalProgress * 1.2,
  //   config: config.stiff,
  // })

  const [artBoardOverflowSpring, artBoardOverflowSpringApi] = useSpring(() => ({
    translateY: 200,
    config: config.stiff,
  }))

  const drawLine = (dayInYear: number, length: number) => {
    if (pathRef.current === null) return

    const delta = 0.01
    const coordinates = pathRef.current?.getPointAtLength(
      dayLength * dayInYear
    )!
    const pointBefore = pathRef.current?.getPointAtLength(
      dayLength * dayInYear - delta
    )!
    const pointAfter = pathRef.current?.getPointAtLength(
      dayLength * dayInYear + delta
    )!
    const slope =
      (pointAfter.y - pointBefore.y) / (pointAfter.x - pointBefore.x)

    // Calculate the slope of the perpendicular line
    const perpendicularSlope = -1 / slope

    // Determine the end points of the perpendicular line (adjust the length as needed)
    const angle = Math.atan(perpendicularSlope)

    return {
      x1: coordinates.x! + length * Math.cos(angle),
      y1: coordinates.y! + length * Math.sin(angle),
      x2: coordinates.x! - length * Math.cos(angle),
      y2: coordinates.y! - length * Math.sin(angle),
    }
  }

  const pathDates = useMemo(
    () =>
      dates.reduce((a: any, b: any) => {
        return [
          ...b.dates.reduce(
            (x: any, y: any) => [
              { date: y, range: [b.pathRange[0], b.pathRange[1]] },
              ...x,
            ],
            []
          ),
          ...a,
        ]
      }, []),
    [dates]
  ).map((d: any) => (
    <PathEventMarker
      dayInYear={getDayInYear(d.date)}
      dayLength={dayLength}
      day={day}
      path={pathRef.current}
      range={[getDayInYear(d.range[0]), getDayInYear(d.range[1])]}
      secondary={getDayInYear(d.date) === 65}
    />
  ))

  useEffect(() => {
    pathLengthRef.current = pathLength
  }, [pathLength])

  useEffect(() => {
    pathStepRef.current = pathStep
  }, [pathStep])

  useEffect(() => {
    const m = [
      getDayInYear(new Date('2024-02-01')),
      getDayInYear(new Date('2024-03-01')),
      getDayInYear(new Date('2024-04-01')),
      getDayInYear(new Date('2024-05-01')),
      getDayInYear(new Date('2024-06-01')),
      getDayInYear(new Date('2024-07-01')),
      getDayInYear(new Date('2024-08-01')),
      getDayInYear(new Date('2024-09-01')),
      getDayInYear(new Date('2024-10-01')),
      getDayInYear(new Date('2024-11-01')),
      getDayInYear(new Date('2024-12-01')),
      getDayInYear(new Date('2025-01-01')),
      getDayInYear(new Date('2025-02-01')),
    ]

    months.current = m.map((i) => ({
      month: i,
      point: pathRef.current?.getPointAtLength(dayLength * i),
      textPosition: dayLength * i,
      name: new Date(2024, i, 1).toLocaleDateString('fr-CH', {
        month: 'short',
      }),
    }))

    days.current = new Array(daysCount).fill(0).map((_, i) => ({
      month: i,
      point: pathRef.current?.getPointAtLength(dayLength * i),
    }))

    if (pathRef.current) {
      setPathLength(pathRef.current.getTotalLength())
    }
  }, [pathRef.current, width, height])

  const onStepProgress = (stepData: any) => {
    if (stepData.step < 0) return

    const chartOverflow =
      window.innerHeight - (svgRef.current?.getBoundingClientRect().height || 0)
    const step = stepData.data
    const progress = stepData.progress
    const dayLength = pathLengthRef.current / daysCount
    const totalProgress =
      step / dates.length - (1 / dates.length) * Math.abs(1 - progress)

    // TODO: Store step in ref and change the state only if the step changes
    const day = Math.round(
      dates
        .slice(0, Math.max(0, step - 1))
        .reduce((a, b) => a + b.pathRangeInDays, 0) +
        progress * dates[Math.max(0, step - 1)].pathRangeInDays
    )

    // Move the chart up or down to follow the scroll
    artBoardOverflowSpringApi.start({
      translateY: 200 + (chartOverflow - 200) * totalProgress * 1.2,
    })

    // Animate the main path
    mainPathSpringApi.start({
      strokeDashoffset: pathLengthRef.current - dayLength * day,
    })
    // pathRef.current?.style.setProperty(
    //   'stroke-dashoffset',
    //   `${pathLengthRef.current - dayLength * day}`
    // )

    // setState((prevState) => ({
    //   step: prevState.step !== stepData.data ? stepData.data : prevState.step,
    //   progress: stepData.progress,
    // }))

    if (pathStepRef.current !== step) {
      setPathStep(stepData.data)
    }
  }
  // useEffect(() => {
  //   window.addEventListener('scroll', () => {
  //     if (scrollyTellingContainerRef.current) {
  //       const topPos = scrollyTellingContainerRef.current.getBoundingClientRect().top + window.scrollY
  //       const bottomPos = window.document.body.offsetHeight - topPos - scrollyTellingContainerRef.current.getBoundingClientRect().height
  //       const scrollPct = Math.min(1, Math.max(0, window.scrollY - topPos) / (scrollyTellingContainerRef.current.getBoundingClientRect().height - 1000))
  //       console.log("Top position:", topPos, "Bottom position:", bottomPos)
  //       console.log("Scroll pct:", scrollPct)
  //       console.log("")
  //       throttledHandleScroll(scrollPct)
  //     }
  //   })
  // }, [])

  const throttledHandleScroll = useCallback(throttle(100, onStepProgress), [])
  // useEffect(() => {
  //   const pct = 0.637// getDayInYear(today) / daysCount
  //   if (!scrollyTellingContainerRef.current) return
  //   console.log('T', scrollyTellingContainerRef.current.getBoundingClientRect().top)
  //   console.log('H', scrollyTellingContainerRef.current.getBoundingClientRect().height)
  //   console.log(today, getDayInYear(today), daysCount, pct, "%")

  //   const newScrollY = scrollyTellingContainerRef.current.getBoundingClientRect().top + scrollyTellingContainerRef.current.getBoundingClientRect().height * pct


  //   if (pathLength > 0) {
  //     window.scrollTo(0, newScrollY - window.innerHeight * 0.5)
  //     console.log(newScrollY, getDayInYear(today) / daysCount)
  //   }
  // }, [pathLength])

  return (
    <AppContainer $colorMode={colorMode}>
      {process.env.NODE_ENV !== 'production' && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            lineHeight: 1.2,
            fontSize: '0.85em',
            padding: 15,
          }}
        >
          {pathStep}/{dates.length} ({(state.step / dates.length).toFixed(2)}
          %)
          {/* <br /> {state.progress.toFixed(2)}% <br /> {totalProgress.toFixed(2)}% */}
        </div>
      )}
      {process.env.NODE_ENV !== 'production' && (
        <div style={{ height: 1000, background: '#f2f4f6', width: '100%' }} />
      )}
      <ThemeProvider theme={theme}>
        <Confettis launch={pathStep === 7} />
        <Scrollyrtelling ref={scrollyTellingContainerRef}>
          <StickyChart>
            <Container
              style={{
                transform: artBoardOverflowSpring.translateY.to(
                  (v) => `translate3d(-15%, ${v}px, 0)`
                ),
              }}
              ref={containerRef}
            >
              {/* <Pattern maskUrl={require('./mask-test.svg').default} /> */}
              <Svg
                viewBox='0 0 608.75 1714.44'
                ref={svgRef}
                width={width * 1.35}
                height={height * 1.35}
              >
                <defs>
                  <marker
                    id='arrowRight'
                    markerWidth='10'
                    markerHeight='10'
                    refX='10'
                    refY='5'
                    orient='auto'
                    viewBox='0 0 10 10'
                    fill={theme.backgroundColor}
                  >
                    <path d='M 0 0 L 10 5 L 0 10 Z' />
                  </marker>
                  <marker
                    id='arrowLeft'
                    markerWidth='10'
                    markerHeight='10'
                    refX='0'
                    refY='5'
                    orient='auto'
                    viewBox='0 0 10 10'
                    fill={theme.backgroundColor}
                  >
                    <path d='M 10 0 L 0 5 L 10 10 Z' />
                  </marker>
                </defs>
                {/* Hatch pattern — poor performances on iOS safari */}
                {/* <pattern
                  id='diagonalHatch'
                  patternUnits='userSpaceOnUse'
                  width='4'
                  height='4'
                  patternTransform='rotate(-45 2 2)'
                >
                  <PathPattern d='M -1,2 l 6,0' />
                </pattern> */}

                {/* Ranges Area */}
                {/* {dates.map((d) => {
                  if (d.range.length === 0) return

                  const start = getDayInYear(d.range[0])
                  const end = getDayInYear(d.range[1])
                  const diff = end - start
                  const visibleLength = diff * dayLength
                  const startOffset = start * dayLength

                  return (
                    <RangePath
                      strokeDasharray={`${visibleLength} ${
                        pathLength - visibleLength
                      }`}
                      strokeDashoffset={-startOffset}
                      d='m124.62,3.39s-1.52,40.66,12.04,49.13c72.28,60.42,402.35-42.33,399.81,83.25-2.26,111.58-377.13-23.95-379.48,103.91-2.26,122.87,343.55-19.48,343.55,115.18,0,108.16-415.7-3.45-409.05,120.63,6.78,126.49,463.06-19.58,463.06,103.91,0,131.76-381.32-19.48-381.32,109.27,0,146.98,363.67-20.11,368.19,110.91s-423.84-7-423.84,135.31c0,128.75,423.84-9.04,417.06,131.01-6.78,140.05-470.7-20.34-470.7,126.49,0,137.79,402.94-2.52,402.94,126.24s-297.81,20.36-300.42,126.49c-2.67,108.71,370.45-6.52,370.45,119.98,0,119.72-413.83-9.88-413.83,106.16v41.59'
                    />
                  )
                })} */}

                {/* Gray path (in the future) */}
                <GhostPath d='m124.62,3.39s-1.52,40.66,12.04,49.13c72.28,60.42,402.35-42.33,399.81,83.25-2.26,111.58-377.13-23.95-379.48,103.91-2.26,122.87,343.55-19.48,343.55,115.18,0,108.16-415.7-3.45-409.05,120.63,6.78,126.49,463.06-19.58,463.06,103.91,0,131.76-381.32-19.48-381.32,109.27,0,146.98,363.67-20.11,368.19,110.91s-423.84-7-423.84,135.31c0,128.75,423.84-9.04,417.06,131.01-6.78,140.05-470.7-20.34-470.7,126.49,0,137.79,402.94-2.52,402.94,126.24s-297.81,20.36-300.42,126.49c-2.67,108.71,370.45-6.52,370.45,119.98,0,119.72-413.83-9.88-413.83,106.16v41.59' />

                {/* Active path (present or past) */}
                <Path
                  id='main-path'
                  strokeDasharray={pathRef.current?.getTotalLength()}
                  strokeDashoffset={mainPathSpring.strokeDashoffset}
                  ref={pathRef}
                  d='m124.62,3.39s-1.52,40.66,12.04,49.13c72.28,60.42,402.35-42.33,399.81,83.25-2.26,111.58-377.13-23.95-379.48,103.91-2.26,122.87,343.55-19.48,343.55,115.18,0,108.16-415.7-3.45-409.05,120.63,6.78,126.49,463.06-19.58,463.06,103.91,0,131.76-381.32-19.48-381.32,109.27,0,146.98,363.67-20.11,368.19,110.91s-423.84-7-423.84,135.31c0,128.75,423.84-9.04,417.06,131.01-6.78,140.05-470.7-20.34-470.7,126.49,0,137.79,402.94-2.52,402.94,126.24s-297.81,20.36-300.42,126.49c-2.67,108.71,370.45-6.52,370.45,119.98,0,119.72-413.83-9.88-413.83,106.16v41.59'
                />

                {/* Ranges Path */}
                {dates.map((d, i) => {
                  if (d.range.length === 0) return

                  const start = getDayInYear(d.range[0])
                  const end = getDayInYear(d.range[1])
                  const diff = end - start
                  const visibleLength = diff * dayLength
                  const startOffset = start * dayLength

                  return (
                    <SecondaryPath
                      key={`secondary-range-path-${i}`}
                      strokeDasharray={mainPathSpring.strokeDashoffset.to(
                        (totalPathPos) => {
                          const invertedTotalPathPos = pathLength - totalPathPos
                          const rangePos = Math.max(
                            0,
                            invertedTotalPathPos - startOffset
                          )
                          const vl =
                            rangePos > visibleLength ? visibleLength : rangePos
                          return `${vl} 900000`
                        }
                      )}
                      // strokeDasharray={`${visibleLength} ${
                      //   pathLength - visibleLength
                      // }`}
                      strokeDashoffset={-startOffset}
                      ref={pathRef}
                      d='m124.62,3.39s-1.52,40.66,12.04,49.13c72.28,60.42,402.35-42.33,399.81,83.25-2.26,111.58-377.13-23.95-379.48,103.91-2.26,122.87,343.55-19.48,343.55,115.18,0,108.16-415.7-3.45-409.05,120.63,6.78,126.49,463.06-19.58,463.06,103.91,0,131.76-381.32-19.48-381.32,109.27,0,146.98,363.67-20.11,368.19,110.91s-423.84-7-423.84,135.31c0,128.75,423.84-9.04,417.06,131.01-6.78,140.05-470.7-20.34-470.7,126.49,0,137.79,402.94-2.52,402.94,126.24s-297.81,20.36-300.42,126.49c-2.67,108.71,370.45-6.52,370.45,119.98,0,119.72-413.83-9.88-413.83,106.16v41.59'
                    />
                  )
                })}

                {/* Months dividers */}
                {months.current.map((m, i) => {
                  const coordinates = drawLine(m.month, 45)

                  return (
                    <g key={`month-line-${i}`}>
                      <MonthLine {...coordinates} />
                    </g>
                  )
                })}

                {/* Ranges dividers */}
                {/* {dates.map((d) => {
                if (d.range.length === 0) return
                const coordinatesStart = drawLine(getDayInYear(d.range[0]), 50)
                const coordinatesEnd = drawLine(getDayInYear(d.range[1]), 50)

                return (
                  <g>
                    <line
                      stroke='green'
                      strokeWidth={2}
                      {...coordinatesStart}
                    />
                    <line stroke='red' strokeWidth={2} {...coordinatesEnd} />
                  </g>
                )
              })} */}

                {/* Months labels */}
                {/* {months.current.map((m: any) => (
                  <text width='500'>
                    <textPath
                      fontFamily='var(--font-sans-serif)'
                      alignment-baseline='top'
                      xlinkHref='#main-path'
                      startOffset={m.textPosition}
                    >
                      {m.name}
                    </textPath>
                  </text>
                ))} */}
                <Texts>
                  <g id='janvier_2024'>
                    <path d='m320.68,64.31l.42,10.33c.02.59-.05,1.12-.22,1.61-.17.49-.43.91-.77,1.25-.34.35-.75.62-1.24.82-.49.2-1.02.31-1.61.34-1.08.04-1.96-.21-2.64-.75-.68-.55-1.14-1.29-1.36-2.25l1.5-.42c.16.59.43,1.07.82,1.44.39.36.94.53,1.66.5.72-.03,1.27-.27,1.65-.73.38-.46.55-1.13.52-2.03l-.35-8.57-4.8.2-.06-1.48,6.47-.27Z' />
                    <path d='m333.47,77.76l-1.56-4.06-5.64.23-1.23,4.17-1.7.07,4.31-14.15,2.14-.09,5.44,13.75-1.76.07Zm-4.68-12.22h-.1s-2.08,6.89-2.08,6.89l4.82-.19-2.63-6.7Z' />
                    <path d='m340.59,68.96l-1.79-3.04h-.06s.43,11.61.43,11.61l-1.64.06-.52-13.95,1.94-.07,5.41,8.32,1.79,3.04h.06s-.43-11.61-.43-11.61l1.64-.06.52,13.95-1.94.07-5.41-8.32Z' />
                    <path d='m354.81,76.98l-5.08-13.81,1.8-.06,2.52,6.96,1.71,5.19h.1s1.41-5.29,1.41-5.29l2.1-7.11,1.74-.06-4.25,14.1-2.04.06Z' />
                    <path d='m363.11,76.73l-.04-1.4,1.96-.05-.29-11.16-1.96.05-.04-1.4,5.6-.15.04,1.4-1.96.05.29,11.16,1.96-.05.04,1.4-5.6.15Z' />
                    <path d='m371.69,76.51l-.26-13.96,8.52-.16.03,1.48-6.84.13.09,4.68,6.44-.12.03,1.48-6.44.12.09,4.84,6.84-.13.03,1.48-8.52.16Z' />
                    <path d='m384.95,76.3h-1.68s-.11-13.95-.11-13.95l5.56-.04c1.23,0,2.19.32,2.88,1,.69.67,1.04,1.65,1.05,2.93,0,1.03-.22,1.87-.69,2.54-.47.66-1.16,1.11-2.08,1.33l3.25,6.13h-1.9s-3.07-5.96-3.07-5.96l-3.26.02.05,6Zm3.82-7.47c.67,0,1.18-.18,1.55-.53.36-.35.54-.84.54-1.48v-1.04c-.01-.64-.2-1.13-.57-1.48s-.89-.51-1.55-.51l-3.88.03.04,5.04,3.88-.03Z' />
                  </g>
                  <g id='fevrier'>
                    <path d='m276.29,187.01l-.11-13.96,8.32-.07v1.48s-6.63.05-6.63.05l.04,4.68,6.06-.05v1.48s-6.05.05-6.05.05l.05,6.32h-1.68Z' />
                    <path d='m287.36,186.95l.1-13.96,8.52.06v1.48s-6.85-.05-6.85-.05l-.03,4.68,6.44.04v1.48s-6.45-.04-6.45-.04l-.03,4.84,6.84.05v1.48s-8.53-.06-8.53-.06Zm4.47-15.07l-1.04-.49,1.72-3.39,1.46.71-2.14,3.17Z' />
                    <path d='m301.9,187.11l-4.37-14.05,1.8.03,2.16,7.08,1.44,5.27h.1s1.68-5.21,1.68-5.21l2.46-6.99,1.74.03-4.97,13.87-2.04-.04Z' />
                    <path d='m312.51,187.35l-1.68-.05.41-13.95,5.56.16c1.23.04,2.17.4,2.84,1.1.67.7.98,1.69.94,2.97-.03,1.03-.29,1.86-.78,2.51-.49.65-1.2,1.06-2.13,1.25l3.02,6.25-1.9-.06-2.84-6.09-3.26-.1-.18,6Zm4.1-7.32c.67.02,1.19-.14,1.56-.47.38-.34.57-.82.59-1.46l.03-1.04c.02-.64-.15-1.14-.51-1.5s-.87-.55-1.53-.57l-3.88-.11-.15,5.04,3.88.11Z' />
                    <path d='m322.93,187.68l.05-1.4,1.96.07.39-11.15-1.96-.07.05-1.4,5.6.2-.05,1.4-1.96-.07-.39,11.15,1.96.07-.05,1.4-5.6-.2Z' />
                    <path d='m331.55,187.98l.55-13.95,8.51.33-.06,1.48-6.83-.27-.18,4.68,6.44.25-.06,1.48-6.44-.25-.19,4.84,6.83.27-.06,1.48-8.51-.33Z' />
                    <path d='m344.86,188.52l-1.68-.07.59-13.95,5.55.23c1.23.05,2.17.43,2.82,1.14.66.71.96,1.7.91,2.98-.04,1.03-.32,1.86-.82,2.5-.5.64-1.22,1.05-2.14,1.22l2.94,6.29-1.9-.08-2.76-6.12-3.26-.14-.25,5.99Zm4.19-7.27c.67.03,1.19-.12,1.57-.45.38-.33.58-.82.61-1.46l.04-1.04c.03-.64-.14-1.14-.49-1.5-.35-.36-.86-.56-1.53-.58l-3.88-.16-.21,5.04,3.88.16Z' />
                  </g>
                  <g id='mars'>
                    <path d='m383.95,286.74h-.1l-1.18,2.34-3.34,6.08-3.34-6.08-1.18-2.34h-.1v11.76s-1.63,0-1.63,0v-13.96s2.23,0,2.23,0l4,7.52h.1l4.02-7.52h2.16s0,13.96,0,13.96h-1.64s0-11.76,0-11.76Z' />
                    <path d='m397.88,298.77l-1.3-4.15-5.64-.14-1.5,4.08-1.7-.04,5.22-13.83,2.14.05,4.53,14.08-1.76-.04Zm-3.87-12.5h-.1s-2.53,6.74-2.53,6.74l4.82.12-2.19-6.86Z' />
                    <path d='m403.44,299l-1.68-.09.76-13.94,5.55.3c1.22.07,2.16.46,2.81,1.17.65.72.94,1.71.87,2.99-.06,1.03-.34,1.85-.85,2.49-.51.63-1.23,1.03-2.16,1.19l2.86,6.33-1.9-.1-2.69-6.16-3.26-.18-.33,5.99Zm4.28-7.22c.67.04,1.19-.11,1.58-.43.38-.33.59-.81.63-1.45l.06-1.04c.04-.64-.12-1.14-.47-1.51-.35-.37-.85-.57-1.52-.6l-3.87-.21-.28,5.03,3.87.21Z' />
                    <path d='m418.15,300.25c-1.13-.1-2.07-.41-2.83-.91-.76-.5-1.38-1.16-1.87-1.97l1.33-.92c.43.7.93,1.24,1.5,1.63s1.25.63,2.06.7c1,.09,1.78-.08,2.35-.51.57-.43.89-1.04.96-1.84.06-.66-.09-1.19-.46-1.59-.37-.4-1-.74-1.89-1.04l-1.11-.36c-1.25-.41-2.18-.94-2.8-1.59-.62-.65-.87-1.52-.78-2.59.06-.61.22-1.15.49-1.6.27-.46.62-.83,1.06-1.11s.94-.48,1.52-.59c.58-.11,1.21-.14,1.89-.08,1.05.1,1.93.37,2.65.83.71.45,1.3,1.08,1.75,1.87l-1.34.8c-.34-.57-.76-1.03-1.28-1.38-.52-.36-1.18-.57-1.99-.65-.89-.08-1.61.04-2.15.37-.55.33-.85.88-.92,1.63-.06.66.11,1.19.51,1.56.4.38,1.03.71,1.89.99l1.11.36c1.34.43,2.29.99,2.84,1.67.55.68.78,1.54.69,2.57-.06.64-.22,1.21-.49,1.7-.27.5-.62.91-1.07,1.23-.44.32-.97.55-1.58.7-.61.15-1.29.18-2.03.11Z' />
                  </g>
                  <g id='avril'>
                    <path d='m111.55,444.27l-3.92-1.9-3.96,4.01,1.95,3.89-1.19,1.21-6.51-13.28,1.5-1.52,13.36,6.33-1.24,1.25Zm-11.76-5.74l-.07.07,3.18,6.46,3.39-3.43-6.5-3.1Z' />
                    <path d='m115.67,441.38l-11.59-9.06,1.5-1,5.81,4.59,4.18,3.51.08-.06-1.59-5.24-1.97-7.15,1.45-.96,3.81,14.23-1.7,1.13Z' />
                    <path d='m123.94,436.48l-1.52.71-5.86-12.67,5.05-2.33c1.11-.52,2.12-.61,3.03-.28.91.33,1.63,1.07,2.17,2.24.43.93.57,1.8.42,2.59-.15.8-.6,1.49-1.35,2.07l5.49,4.25-1.72.8-5.26-4.18-2.96,1.37,2.52,5.45Zm.4-8.38c.6-.28,1-.65,1.19-1.12s.15-.99-.12-1.57l-.44-.94c-.27-.58-.64-.95-1.12-1.11-.48-.16-1.02-.1-1.62.18l-3.52,1.63,2.12,4.57,3.52-1.63Z' />
                    <path d='m132.94,432.54l-.47-1.32,1.85-.66-3.75-10.51-1.85.66-.47-1.32,5.27-1.88.47,1.32-1.85.66,3.75,10.51,1.85-.66.47,1.32-5.27,1.88Z' />
                    <path d='m140.73,429.85l-3.89-13.41,1.61-.47,3.47,11.99,5.71-1.65.41,1.42-7.32,2.12Z' />
                  </g>
                  <g id='mai'>
                    <path d='m417.53,520.75h-.1s-1.16,2.35-1.16,2.35l-3.29,6.11-3.39-6.05-1.2-2.33h-.1s.1,11.76.1,11.76h-1.64s-.12-13.95-.12-13.95l2.24-.02,4.06,7.49h.1s3.96-7.55,3.96-7.55l2.16-.02.12,13.96h-1.64s-.1-11.75-.1-11.75Z' />
                    <path d='m431.63,532.57l-1.37-4.13-5.64-.05-1.43,4.11h-1.7s4.99-13.93,4.99-13.93l2.14.02,4.77,14h-1.76Zm-4.08-12.43h-.1s-2.41,6.78-2.41,6.78l4.82.04-2.31-6.82Z' />
                    <path d='m434.95,532.63l.03-1.4,1.96.04.25-11.16-1.96-.04.03-1.4,5.6.13-.03,1.4-1.96-.04-.25,11.16,1.96.04-.03,1.4-5.6-.13Z' />
                  </g>
                  <g id='juin'>
                    <path d='m176.74,655.39l8.57,5.78c.49.33.87.71,1.16,1.13.29.43.47.88.55,1.36.08.48.06.97-.07,1.48-.13.51-.35,1.01-.68,1.49-.6.9-1.33,1.46-2.17,1.68-.84.23-1.72.15-2.62-.23l.55-1.46c.57.22,1.12.29,1.64.19s.99-.44,1.39-1.04.53-1.18.39-1.76-.58-1.12-1.33-1.62l-7.11-4.79-2.68,3.98-1.23-.83,3.62-5.37Z' />
                    <path d='m181.52,650.1l5.62,6.58c.87,1.02,1.71,1.62,2.53,1.83.82.2,1.7-.1,2.65-.91.94-.81,1.38-1.63,1.31-2.47-.07-.84-.54-1.77-1.41-2.79l-5.62-6.58,1.28-1.09,5.35,6.26c.66.77,1.16,1.5,1.51,2.18.35.68.53,1.34.55,1.98.02.64-.14,1.25-.48,1.86-.34.6-.86,1.21-1.57,1.81-.71.61-1.38,1.02-2.02,1.25-.64.23-1.26.28-1.88.16-.61-.12-1.23-.42-1.84-.88-.61-.46-1.25-1.07-1.91-1.84l-5.35-6.26,1.28-1.09Z' />
                    <path d='m198.43,654.57l-.69-1.22,1.71-.96-5.49-9.72-1.71.96-.69-1.22,4.88-2.76.69,1.22-1.71.96,5.49,9.72,1.71-.96.69,1.22-4.88,2.76Z' />
                    <path d='m205.19,641.64l-2.69-2.28-.06.02,4.2,10.83-1.53.59-5.04-13.02,1.81-.7,7.83,6.1,2.69,2.28.06-.02-4.2-10.83,1.53-.59,5.04,13.02-1.81.7-7.83-6.1Z' />
                  </g>
                  <g id='juillet'>
                    <path d='m510.71,747.62l-3.43,9.76c-.19.55-.46,1.02-.8,1.41-.34.39-.73.68-1.18.88-.44.2-.93.3-1.45.31-.53,0-1.06-.09-1.62-.28-1.02-.36-1.74-.92-2.18-1.68-.43-.76-.58-1.62-.44-2.59l1.55.16c-.07.61,0,1.16.23,1.64.23.48.68.84,1.36,1.08.68.24,1.28.22,1.8-.07.52-.28.93-.85,1.23-1.69l2.84-8.09-4.53-1.59.49-1.4,6.11,2.15Z' />
                    <path d='m516.48,750.12l-4.12,7.61c-.64,1.17-.9,2.18-.79,3.02.11.84.71,1.55,1.8,2.14,1.09.59,2.02.7,2.78.33.76-.37,1.46-1.14,2.09-2.32l4.12-7.61,1.48.8-3.92,7.25c-.48.89-.98,1.62-1.49,2.19-.51.57-1.06.98-1.65,1.22-.59.24-1.22.32-1.9.22s-1.44-.37-2.26-.81c-.82-.44-1.45-.92-1.9-1.44-.44-.51-.72-1.08-.82-1.7-.11-.62-.05-1.29.16-2.03.21-.74.55-1.55,1.04-2.44l3.92-7.25,1.48.8Z' />
                    <path d='m518.44,767.42l.89-1.08,1.51,1.25,7.12-8.59-1.51-1.25.89-1.08,4.31,3.57-.89,1.08-1.51-1.25-7.12,8.59,1.51,1.25-.89,1.08-4.31-3.57Z' />
                    <path d='m523.97,772.15l10.87-8.76,1.05,1.31-9.71,7.83,3.73,4.62-1.15.93-4.78-5.93Z' />
                    <path d='m529.04,778.84l12.62-5.97.72,1.52-11.28,5.34,2.54,5.37-1.34.63-3.26-6.89Z' />
                    <path d='m532.47,786.54l13.65-2.94,1.8,8.33-1.45.31-1.44-6.69-4.57.99,1.36,6.3-1.45.31-1.36-6.3-4.73,1.02,1.44,6.69-1.45.31-1.8-8.33Z' />
                    <path d='m546.9,801.44l-12.47-.4.05-1.68,12.47.4.14-4.44,1.48.05-.34,10.55-1.48-.05.14-4.44Z' />
                  </g>
                  <g id='aout'>
                    <path d='m173.94,995.13l-.36-4.34-5.47-1.37-2.36,3.66-1.65-.41,8.13-12.36,2.08.52,1.34,14.73-1.71-.43Zm-1.04-13.04l-.1-.02-3.94,6.02,4.68,1.17-.64-7.17Z' />
                    <path d='m183.17,997.21c-.89-.16-1.67-.46-2.33-.9-.66-.44-1.19-1-1.58-1.69-.4-.69-.65-1.5-.76-2.44-.11-.94-.07-1.98.15-3.14.21-1.15.53-2.15.97-2.97.44-.83.96-1.5,1.58-2.01.61-.51,1.31-.86,2.08-1.03.77-.18,1.6-.19,2.49-.02.88.16,1.65.46,2.32.9.66.44,1.19,1.01,1.59,1.7.39.7.65,1.51.76,2.44.11.93.07,1.97-.14,3.12-.21,1.15-.53,2.15-.97,2.98-.44.83-.96,1.5-1.58,2.01-.61.5-1.31.85-2.09,1.02-.78.18-1.61.18-2.48.02Zm.27-1.46c.59.11,1.15.1,1.69-.02.54-.12,1.02-.34,1.45-.67.43-.33.8-.75,1.1-1.27.3-.52.51-1.12.63-1.8l.42-2.29c.12-.68.14-1.32.04-1.91-.1-.59-.29-1.11-.58-1.57-.29-.46-.66-.84-1.12-1.14-.46-.3-.99-.51-1.58-.61-.59-.11-1.15-.1-1.69.02-.54.12-1.02.34-1.45.67-.43.33-.8.75-1.1,1.27-.3.52-.51,1.12-.63,1.8l-.42,2.29c-.12.68-.14,1.32-.04,1.91.1.59.29,1.11.58,1.57.29.46.66.84,1.12,1.14.46.3.99.51,1.58.61Z' />
                    <path d='m195.73,984.88l-1.13,8.58c-.17,1.32-.06,2.36.34,3.11.4.75,1.21,1.2,2.44,1.36,1.23.16,2.13-.07,2.71-.68.58-.62.95-1.59,1.13-2.91l1.13-8.58,1.67.22-1.07,8.17c-.13,1-.34,1.87-.61,2.58-.28.72-.64,1.29-1.1,1.73-.46.44-1.03.73-1.7.89-.67.15-1.47.17-2.4.05-.93-.12-1.69-.34-2.28-.66-.6-.32-1.05-.75-1.37-1.29-.32-.54-.51-1.19-.58-1.95-.07-.76-.04-1.65.1-2.65l1.07-8.17,1.67.22Zm4.88-4.14l1.83,3.12-.95.5-2.02-2.62-2.62,2.02-.79-.73,2.57-2.55,1.98.26Z' />
                    <path d='m211.93,988.25l-1.2,12.42-1.67-.16,1.2-12.42-4.42-.43.14-1.47,10.51,1.01-.14,1.47-4.42-.43Z' />
                  </g>
                  <g id='septembre'>
                    <path d='m336.14,1135.16c-1.13-.03-2.09-.28-2.88-.74-.79-.46-1.45-1.07-1.99-1.85l1.27-1c.47.67,1.01,1.18,1.59,1.54s1.29.55,2.1.57c1,.03,1.77-.19,2.31-.65.54-.46.82-1.1.85-1.9.02-.67-.16-1.19-.55-1.56-.39-.37-1.04-.68-1.95-.92l-1.13-.29c-1.27-.33-2.23-.8-2.89-1.42-.66-.61-.97-1.46-.93-2.54.02-.61.15-1.16.39-1.63.24-.47.57-.86.99-1.17.42-.31.91-.54,1.48-.69.57-.15,1.2-.21,1.88-.19,1.05.03,1.95.25,2.69.66.74.41,1.36.99,1.86,1.76l-1.29.88c-.37-.54-.82-.98-1.36-1.3-.54-.32-1.21-.5-2.03-.52-.89-.03-1.6.14-2.13.51-.52.36-.8.93-.82,1.69-.02.67.18,1.18.6,1.53.42.35,1.07.64,1.94.87l1.13.29c1.36.35,2.34.84,2.94,1.49.59.65.88,1.49.84,2.53-.02.64-.15,1.22-.38,1.73-.24.51-.57.94-.99,1.29-.42.35-.94.61-1.53.79-.6.18-1.27.26-2.02.24Z' />
                    <path d='m343.88,1135.15l.35-13.96,8.52.21-.04,1.48-6.84-.17-.12,4.68,6.44.16-.04,1.48-6.44-.16-.12,4.84,6.84.17-.04,1.48-8.52-.21Z' />
                    <path d='m355.59,1135.43l.25-13.96,5.56.1c1.25.02,2.21.4,2.87,1.12.66.73.98,1.71.96,2.95-.02,1.24-.38,2.21-1.06,2.91-.69.7-1.66,1.04-2.91,1.02l-3.88-.07-.11,5.96-1.68-.03Zm1.81-7.41l3.88.07c.67.01,1.19-.15,1.56-.49.37-.34.56-.83.58-1.47l.02-1.04c.01-.64-.16-1.14-.52-1.49-.36-.35-.87-.54-1.54-.55l-3.88-.07-.09,5.04Z' />
                    <path d='m372.58,1123.19l-.11,12.48-1.68-.02.11-12.48-4.44-.04v-1.48s10.57.1,10.57.1v1.48s-4.45-.04-4.45-.04Z' />
                    <path d='m379.28,1135.72l-.02-13.96h8.52s0,1.47,0,1.47h-6.84s0,4.69,0,4.69h6.44s0,1.47,0,1.47h-6.44s0,4.85,0,4.85h6.84s0,1.47,0,1.47h-8.52Z' />
                    <path d='m401.73,1123.75h-.1s-1.14,2.36-1.14,2.36l-3.24,6.14-3.44-6.02-1.22-2.32h-.1s.2,11.76.2,11.76l-1.64.03-.24-13.96,2.24-.04,4.13,7.45h.1s3.89-7.59,3.89-7.59l2.16-.04.24,13.96-1.64.03-.2-11.76Z' />
                    <path d='m406.89,1121.41l5.74-.22c1.16-.04,2.08.25,2.77.87.68.63,1.05,1.49,1.09,2.58.02.52-.04.96-.17,1.33-.13.37-.31.67-.53.91-.22.24-.46.42-.72.55-.26.12-.51.21-.73.26v.12c.26,0,.54.06.86.17.32.11.62.29.91.54s.54.57.75.97c.21.4.32.89.35,1.46.02.57-.05,1.11-.21,1.61-.16.5-.39.94-.7,1.31-.31.37-.67.67-1.11.89-.43.22-.91.35-1.43.37l-6.34.24-.54-13.95Zm1.91,5.97l3.86-.15c.67-.03,1.18-.21,1.54-.56.36-.35.53-.83.5-1.46l-.03-.68c-.02-.63-.23-1.1-.61-1.42-.39-.32-.91-.47-1.58-.44l-3.86.15.17,4.56Zm.25,6.44l4.14-.16c.72-.03,1.28-.23,1.68-.61s.58-.93.56-1.63l-.03-.68c-.03-.71-.25-1.23-.68-1.58-.43-.35-1-.51-1.72-.48l-4.14.16.19,5Z' />
                    <path d='m422.32,1134.71l-1.68.1-.86-13.93,5.55-.34c1.22-.08,2.2.2,2.93.84.73.64,1.13,1.59,1.21,2.87.06,1.02-.12,1.88-.55,2.57-.43.69-1.1,1.17-2,1.44l3.57,5.95-1.9.12-3.39-5.8-3.25.2.37,5.99Zm3.41-7.67c.67-.04,1.17-.25,1.51-.61.34-.37.5-.87.46-1.51l-.06-1.04c-.04-.64-.25-1.12-.64-1.44-.39-.32-.91-.46-1.58-.42l-3.87.24.31,5.03,3.87-.24Z' />
                    <path d='m433.6,1133.95l-1.25-13.9,8.49-.76.13,1.47-6.81.61.42,4.66,6.41-.57.13,1.47-6.41.57.43,4.82,6.81-.61.13,1.47-8.49.76Z' />
                  </g>
                  <g id='octobre'>
                    <path d='m84.23,1243.36c-.76-.5-1.36-1.07-1.79-1.73-.44-.66-.7-1.38-.8-2.17-.1-.79-.02-1.64.24-2.54.26-.91.71-1.85,1.35-2.83.64-.98,1.32-1.77,2.05-2.36.72-.59,1.47-1.01,2.23-1.24.76-.23,1.54-.28,2.32-.15.78.14,1.55.45,2.31.95.75.49,1.34,1.07,1.78,1.73.44.66.71,1.39.8,2.19.09.79.01,1.64-.24,2.54-.25.9-.7,1.84-1.35,2.82-.64.98-1.33,1.77-2.05,2.37-.73.6-1.47,1.01-2.23,1.24-.76.23-1.54.27-2.32.13-.79-.14-1.55-.45-2.3-.94Zm.81-1.24c.5.33,1.02.54,1.56.64.54.1,1.07.08,1.6-.06.53-.14,1.03-.38,1.5-.74.48-.36.9-.83,1.28-1.41l1.27-1.95c.38-.58.64-1.16.78-1.74.14-.58.17-1.14.08-1.67-.09-.54-.28-1.03-.59-1.49s-.71-.85-1.21-1.18c-.5-.33-1.02-.54-1.56-.64-.54-.1-1.08-.08-1.6.06-.53.14-1.03.38-1.5.74-.47.36-.9.83-1.28,1.41l-1.27,1.95c-.38.58-.64,1.16-.78,1.74s-.17,1.14-.08,1.67c.09.54.28,1.03.59,1.49.31.46.71.85,1.21,1.18Z' />
                    <path d='m97.46,1250.45c-1.62-.72-2.64-1.83-3.05-3.34-.41-1.5-.14-3.33.82-5.47s2.14-3.59,3.55-4.34c1.41-.75,2.93-.76,4.55-.04,1.07.48,1.86,1.12,2.37,1.92.51.8.77,1.74.77,2.81l-1.58.2c.04-.77-.12-1.47-.47-2.1-.35-.63-.92-1.12-1.71-1.47-.55-.24-1.08-.36-1.61-.34s-1.02.15-1.49.4c-.47.25-.9.61-1.3,1.07s-.74,1.02-1.03,1.68l-.85,1.91c-.59,1.32-.74,2.49-.47,3.51.28,1.02.97,1.77,2.08,2.27.82.36,1.59.46,2.33.28.74-.18,1.39-.55,1.95-1.13l.88,1.31c-.8.72-1.7,1.17-2.68,1.35-.98.17-2.01.02-3.08-.45Z' />
                    <path d='m113.51,1242.9l-3.87,11.86-1.6-.52,3.87-11.86-4.22-1.38.46-1.41,10.04,3.28-.46,1.41-4.22-1.38Z' />
                    <path d='m121.26,1258.24c-.88-.21-1.64-.56-2.27-1.03-.63-.47-1.13-1.07-1.49-1.78-.36-.71-.57-1.53-.63-2.48-.06-.94.04-1.98.32-3.12.27-1.14.65-2.11,1.13-2.92.48-.8,1.04-1.44,1.69-1.92.64-.48,1.35-.78,2.13-.92.78-.13,1.61-.1,2.49.12.87.21,1.62.55,2.26,1.03.64.48,1.14,1.07,1.49,1.79.36.72.57,1.54.63,2.48.06.93-.04,1.97-.32,3.11-.27,1.14-.65,2.12-1.14,2.93-.48.81-1.04,1.45-1.69,1.92-.64.47-1.35.77-2.14.9-.79.13-1.61.1-2.48-.11Zm.35-1.44c.58.14,1.15.17,1.69.08.54-.09,1.04-.29,1.49-.59.45-.3.84-.71,1.16-1.21.33-.5.57-1.09.73-1.76l.54-2.26c.16-.68.21-1.31.15-1.9-.06-.59-.23-1.13-.49-1.6-.26-.48-.62-.88-1.06-1.2-.44-.33-.96-.56-1.54-.7-.58-.14-1.15-.17-1.69-.08-.54.09-1.04.29-1.49.59-.45.3-.84.71-1.16,1.21-.33.5-.57,1.09-.73,1.76l-.54,2.26c-.16.68-.21,1.31-.15,1.9.06.59.23,1.13.49,1.6.26.48.61.88,1.06,1.2.44.33.96.56,1.54.7Z' />
                    <path d='m132.87,1246.28l5.66.98c1.14.2,1.98.67,2.52,1.43.54.76.72,1.67.53,2.75-.09.51-.24.93-.44,1.26-.21.33-.44.59-.7.78-.26.19-.54.32-.82.39-.28.07-.54.1-.77.1l-.02.12c.25.06.52.17.8.34.29.17.55.41.78.71.23.3.41.67.53,1.11.12.43.13.93.04,1.5s-.28,1.08-.54,1.53c-.26.46-.58.83-.96,1.13-.38.3-.8.52-1.27.64-.47.13-.96.15-1.47.06l-6.25-1.08,2.38-13.76Zm-.47,12.58l4.08.71c.71.12,1.3.04,1.77-.25.47-.29.76-.78.89-1.48l.12-.67c.12-.7,0-1.26-.34-1.69-.34-.43-.87-.71-1.58-.83l-4.08-.7-.85,4.93Zm1.1-6.35l3.8.66c.66.11,1.2.04,1.62-.23.42-.26.69-.71.8-1.32l.12-.67c.11-.62,0-1.12-.31-1.51-.31-.39-.8-.65-1.45-.76l-3.8-.66-.78,4.49Z' />
                    <path d='m145.36,1262.41l-1.67-.21,1.73-13.85,5.52.69c1.22.15,2.12.61,2.72,1.37s.82,1.77.66,3.05c-.13,1.02-.47,1.83-1.02,2.42-.55.6-1.3.94-2.24,1.04l2.41,6.51-1.89-.24-2.25-6.33-3.23-.4-.75,5.95Zm4.77-6.9c.66.08,1.2-.02,1.6-.32.41-.3.65-.77.73-1.4l.13-1.03c.08-.63-.04-1.15-.36-1.54-.32-.39-.81-.63-1.47-.71l-3.85-.48-.63,5,3.85.48Z' />
                    <path d='m156.66,1263.74l1.26-13.9,8.49.77-.13,1.47-6.81-.62-.42,4.66,6.41.58-.13,1.47-6.41-.58-.44,4.82,6.81.62-.13,1.47-8.49-.77Z' />
                  </g>
                  <g id='novembre'>
                    <path d='m333.6,1388.25l-1.81-3.03h-.06s.48,11.61.48,11.61l-1.64.07-.58-13.95,1.94-.08,5.45,8.3,1.81,3.03h.06s-.48-11.61-.48-11.61l1.64-.07.58,13.95-1.94.08-5.45-8.3Z' />
                    <path d='m350.07,1396.23c-.91.05-1.73-.06-2.47-.33-.74-.27-1.39-.69-1.94-1.27-.55-.58-.99-1.31-1.32-2.19-.33-.88-.53-1.91-.6-3.08-.07-1.17.01-2.21.24-3.12.23-.91.58-1.68,1.06-2.32.48-.64,1.07-1.14,1.78-1.5.71-.35,1.51-.56,2.42-.61.89-.05,1.71.06,2.46.33.75.27,1.4.7,1.95,1.28.55.58.99,1.31,1.32,2.19s.53,1.9.6,3.07c.07,1.17-.01,2.21-.24,3.13-.23.92-.58,1.69-1.06,2.33-.48.64-1.07,1.13-1.79,1.49-.71.35-1.52.56-2.41.61Zm-.09-1.48c.6-.03,1.15-.17,1.64-.42.49-.24.91-.58,1.25-1,.34-.42.6-.92.76-1.49.17-.57.23-1.21.19-1.9l-.14-2.32c-.04-.69-.18-1.32-.41-1.87s-.54-1.01-.93-1.39c-.39-.38-.84-.66-1.36-.85-.52-.18-1.08-.26-1.68-.22-.6.04-1.15.17-1.64.42-.49.24-.91.58-1.25,1-.34.42-.6.92-.76,1.49-.17.57-.23,1.21-.19,1.9l.13,2.32c.04.69.18,1.32.41,1.87.23.55.54,1.01.93,1.39.39.38.84.66,1.36.85.52.18,1.08.26,1.68.22Z' />
                    <path d='m362.05,1395.18l-5.74-13.54,1.79-.14,2.86,6.83,1.95,5.1h.1s1.16-5.36,1.16-5.36l1.75-7.2,1.73-.14-3.57,14.29-2.03.16Z' />
                    <path d='m371.19,1394.4l-1.5-13.88,8.47-.91.16,1.47-6.8.73.5,4.65,6.4-.69.16,1.47-6.4.69.52,4.81,6.8-.73.16,1.47-8.47.91Z' />
                    <path d='m392.07,1379.83l-.1.02-.81,2.49-2.39,6.51-4.22-5.51-1.52-2.14-.1.02,1.77,11.63-1.62.25-2.1-13.8,2.21-.34,5.08,6.83.1-.02,2.84-8.04,2.14-.32,2.1,13.8-1.62.25-1.77-11.63Z' />
                    <path d='m396.58,1376.79l5.61-1.22c1.13-.25,2.09-.12,2.88.38.78.5,1.29,1.28,1.52,2.35.11.51.13.95.07,1.34-.07.38-.19.71-.36.99-.17.28-.38.5-.61.67-.24.17-.46.29-.67.38l.03.12c.25-.04.54-.04.87.01.33.05.66.17.99.37.33.19.63.47.91.83.28.36.47.81.6,1.38.12.56.15,1.1.08,1.62-.07.52-.22.99-.46,1.41-.24.42-.55.78-.93,1.07-.39.3-.83.5-1.34.61l-6.2,1.35-2.96-13.64Zm2.92,5.55l3.77-.82c.65-.14,1.12-.42,1.42-.82s.37-.91.24-1.53l-.14-.66c-.13-.61-.42-1.04-.85-1.29-.44-.25-.98-.3-1.63-.16l-3.77.82.97,4.46Zm1.37,6.29l4.05-.88c.7-.15,1.22-.45,1.54-.9.33-.45.41-1.01.26-1.7l-.14-.66c-.15-.69-.47-1.17-.95-1.44-.48-.27-1.07-.33-1.78-.18l-4.05.88,1.06,4.89Z' />
                    <path d='m414.44,1386.91l-1.61.48-4.01-13.37,5.33-1.6c1.18-.35,2.19-.3,3.04.15.85.45,1.46,1.29,1.83,2.52.29.98.31,1.86.05,2.63-.26.77-.81,1.39-1.63,1.85l4.83,4.98-1.82.54-4.61-4.88-3.12.94,1.72,5.75Zm1.58-8.24c.64-.19,1.08-.51,1.34-.94.25-.44.29-.96.1-1.58l-.3-1c-.18-.61-.5-1.03-.95-1.26-.45-.23-1-.24-1.63-.05l-3.72,1.11,1.45,4.83,3.72-1.11Z' />
                    <path d='m425.69,1383.32l-5.31-12.91,7.88-3.24.56,1.37-6.33,2.6,1.78,4.33,5.96-2.45.56,1.37-5.96,2.45,1.84,4.48,6.33-2.6.56,1.37-7.88,3.24Z' />
                  </g>
                  <g id='decembre'>
                    <path d='m308.33,1491.12l4.68.05c.87,0,1.65.17,2.35.47.7.3,1.3.75,1.79,1.33.49.58.87,1.32,1.12,2.19.26.88.38,1.89.36,3.05-.01,1.16-.16,2.18-.44,3.04-.28.87-.67,1.59-1.18,2.17-.51.57-1.12,1-1.83,1.29-.71.29-1.5.42-2.36.41l-4.68-.05.16-13.96Zm4.53,12.53c.57,0,1.1-.08,1.58-.27.48-.19.9-.47,1.25-.83.35-.37.63-.82.83-1.36.2-.54.3-1.15.31-1.85l.03-2.28c0-.69-.08-1.31-.27-1.85-.19-.54-.45-1-.79-1.38-.34-.38-.75-.67-1.23-.86-.48-.2-1-.3-1.58-.31l-3-.03-.13,11,3,.04Z' />
                    <path d='m321.6,1505.22l.1-13.96,8.52.06v1.48s-6.85-.05-6.85-.05l-.03,4.68,6.44.05v1.48s-6.45-.05-6.45-.05l-.04,4.84,6.84.05v1.48s-8.53-.06-8.53-.06Zm4.47-15.07l-1.04-.49,1.72-3.39,1.45.71-2.14,3.16Z' />
                    <path d='m338.2,1505.57c-1.77,0-3.15-.62-4.13-1.83-.98-1.21-1.46-2.99-1.45-5.34s.52-4.15,1.51-5.4c.99-1.25,2.38-1.88,4.15-1.87,1.17,0,2.16.28,2.95.81.79.53,1.4,1.28,1.84,2.26l-1.36.81c-.28-.72-.7-1.3-1.27-1.74-.57-.44-1.29-.66-2.16-.66-.6,0-1.14.11-1.61.33-.47.22-.88.55-1.21.97-.33.42-.58.92-.76,1.5s-.27,1.23-.27,1.96v2.09c-.02,1.45.31,2.58.97,3.4.66.82,1.6,1.23,2.81,1.24.89,0,1.64-.22,2.24-.68.6-.46,1.05-1.06,1.33-1.82l1.34.85c-.45.98-1.08,1.75-1.91,2.31-.83.56-1.83.83-3,.82Z' />
                    <path d='m345.69,1505.37l.09-13.96,8.52.05v1.48s-6.85-.04-6.85-.04l-.03,4.68,6.44.04v1.48s-6.45-.04-6.45-.04l-.03,4.84,6.84.04v1.48s-8.53-.05-8.53-.05Z' />
                    <path d='m368.32,1493.79h-.1s-1.2,2.33-1.2,2.33l-3.4,6.05-3.28-6.11-1.16-2.35h-.1s-.12,11.76-.12,11.76l-1.64-.02.14-13.96,2.24.02,3.92,7.56h.1s4.1-7.48,4.1-7.48l2.16.02-.14,13.96-1.64-.02.12-11.76Z' />
                    <path d='m373.77,1491.66l5.74.1c1.16.02,2.06.36,2.71,1.03s.96,1.54.94,2.64c0,.52-.09.96-.24,1.32-.15.36-.34.65-.58.88-.23.23-.48.4-.75.51-.27.11-.52.18-.74.22v.12c.25.02.53.09.84.21.31.12.6.32.88.58.28.27.51.6.69,1.01.19.41.27.9.26,1.47s-.11,1.11-.3,1.6c-.19.49-.45.91-.77,1.27-.33.35-.71.63-1.15.83-.44.2-.93.29-1.45.29l-6.34-.11.24-13.96Zm1.46,12.51l4.14.07c.72.01,1.29-.16,1.71-.52.42-.36.64-.89.65-1.6v-.68c.02-.71-.17-1.25-.58-1.62-.41-.37-.97-.57-1.69-.58l-4.14-.07-.09,5Zm.11-6.44l3.86.07c.67.01,1.19-.15,1.57-.47.38-.33.57-.8.59-1.43v-.68c.02-.63-.16-1.11-.52-1.45-.37-.34-.88-.52-1.55-.53l-3.86-.07-.08,4.56Z' />
                    <path d='m388.18,1505.91l-1.68-.05.38-13.96,5.56.15c1.23.03,2.17.4,2.84,1.1.67.7.98,1.69.95,2.97-.03,1.03-.29,1.86-.78,2.51-.49.65-1.2,1.06-2.12,1.25l3.03,6.25-1.9-.05-2.85-6.08-3.26-.09-.16,6Zm4.08-7.33c.67.02,1.19-.14,1.56-.48.38-.34.57-.83.59-1.46l.03-1.04c.02-.64-.15-1.14-.51-1.5-.36-.36-.87-.54-1.54-.56l-3.88-.11-.14,5.04,3.88.11Z' />
                    <path d='m399.2,1506.23l.56-13.95,8.51.34-.06,1.48-6.83-.27-.19,4.68,6.43.26-.06,1.48-6.43-.26-.19,4.84,6.83.27-.06,1.48-8.51-.34Z' />
                  </g>
                  <g id='janvier'>
                    <path d='m303.67,1611.84l-.25,10.34c-.01.59-.12,1.12-.33,1.59-.2.48-.49.88-.85,1.2-.36.33-.79.57-1.29.74-.5.17-1.04.25-1.63.23-1.08-.03-1.94-.33-2.59-.92s-1.05-1.37-1.21-2.33l1.53-.32c.12.6.36,1.1.72,1.49.36.39.91.59,1.63.61s1.28-.19,1.7-.62c.41-.43.63-1.09.65-1.99l.2-8.57-4.8-.11.04-1.48,6.48.15Z' />
                    <path d='m315.56,1626.11l-1.29-4.16-5.64-.15-1.51,4.08-1.7-.04,5.25-13.82,2.14.06,4.5,14.08-1.76-.05Zm-3.85-12.51h-.1s-2.54,6.73-2.54,6.73l4.82.13-2.18-6.86Z' />
                    <path d='m323.26,1617.81l-1.59-3.15h-.06s-.33,11.61-.33,11.61l-1.64-.05.4-13.95,1.94.05,4.86,8.66,1.59,3.15h.06s.33-11.61.33-11.61l1.64.05-.4,13.95-1.94-.05-4.86-8.66Z' />
                    <path d='m336.95,1626.72l-4.25-14.08,1.8.05,2.1,7.1,1.39,5.28h.1s1.72-5.19,1.72-5.19l2.51-6.97,1.74.05-5.08,13.83-2.04-.06Z' />
                    <path d='m345.31,1626.95l.04-1.4,1.96.05.29-11.16-1.96-.05.04-1.4,5.6.14-.04,1.4-1.96-.05-.29,11.16,1.96.05-.04,1.4-5.6-.14Z' />
                    <path d='m353.97,1627.17l.32-13.96,8.52.19-.03,1.48-6.84-.16-.11,4.68,6.44.15-.03,1.48-6.44-.15-.11,4.84,6.84.16-.03,1.48-8.52-.19Z' />
                    <path d='m367.34,1627.45l-1.68-.03.24-13.96,5.56.1c1.23.02,2.18.38,2.85,1.07.67.69,1,1.68.98,2.96-.02,1.03-.27,1.87-.75,2.52-.48.65-1.19,1.08-2.11,1.27l3.09,6.21-1.9-.03-2.92-6.05-3.26-.06-.1,6Zm4.01-7.37c.67.01,1.19-.15,1.56-.49.37-.34.56-.83.58-1.47l.02-1.04c.01-.64-.16-1.14-.52-1.49-.36-.35-.87-.54-1.54-.55l-3.88-.07-.09,5.04,3.88.07Z' />
                  </g>
                </Texts>

                {/* Today's mark */}
                <TodayLine {...drawLine(getDayInYear(today), 30)} />

                {/* Event markers */}
                {pathDates.map((d: any) => d)}
              </Svg>
            </Container>
          </StickyChart>
          {/* {pathStep > 1 && pathStep < 7 && (
            <Legend>
              <LegendItem>
              <div
                style={{
                    display: "inline-block",
                    height: 0,
                    width: 0,
                    borderTop: "4px solid transparent",
                    borderBottom: "6px solid transparent",
                    borderLeft: "10px solid var(--text-color)",
                    marginRight: 5,
                }}
              />
              Aujourd'hui
              </LegendItem>
            </Legend>
          )} */}
          <ScrollytellingContainer>
            <Scrollama offset={1} onStepProgress={throttledHandleScroll}>
              {/* <Step data={0} key={`scrollama-step-${0}`}>
                <StepContainer style={{ height: '25vh' }} />
              </Step> */}
              {dates.map((d, i) => {
                const stepIndex = i + 1
                return (
                  <Step data={stepIndex} key={`scrollama-step-${stepIndex}`}>
                    <StepContainer>
                      <StepContent>
                        <DateLabel>
                          {d.dates.length > 0 && (
                            <Calendar
                              secondary={[1, 2, 3, 6].includes(stepIndex)}
                              dates={d.dates}
                            />
                          )}
                          {d.range.length > 0 && d.dates.length === 0 && (
                            <Calendar
                              secondary={[1, 2, 3, 6].includes(stepIndex)}
                              dates={[d.range[0], d.range[1]]}
                            />
                          )}
                        </DateLabel>
                        {/* <strong>{d.title}</strong> */}
                        <StepText>
                          <ExpandableContent
                            secondary={[1, 2, 3, 6].includes(stepIndex)}
                            maxHeight={0.5}
                          >
                            <div>
                              {d.text}
                              {d.articles.length > 0 && (
                                <RelatedLinks>
                                  <b>À lire sur ce sujet</b>
                                  <List>
                                    {d.articles.map((a: any, i: number) => (
                                      <ListItem key={`related-article-${i}`}>
                                        <Link
                                          secondary={[1, 2, 3, 6].includes(
                                            stepIndex
                                          )}
                                          target='_blank'
                                          href={`https://${tenant.key}.ch/${a[0]}`}
                                        >
                                          {a[1]}
                                        </Link>
                                      </ListItem>
                                    ))}
                                  </List>
                                </RelatedLinks>
                              )}
                            </div>
                          </ExpandableContent>
                        </StepText>
                      </StepContent>
                    </StepContainer>
                  </Step>
                )
              })}
              {/* <Step data={-1} key={`scrollama-step-${0}`}>
                <StepContainer style={{ height: '25vh' }} />
              </Step> */}
            </Scrollama>
          </ScrollytellingContainer>
        </Scrollyrtelling>
      </ThemeProvider>
      {process.env.NODE_ENV !== 'production' && (
        <div
          style={{ height: 1000, background: '#f2f4f6', width: '100%' }}
        />
      )}
    </AppContainer>
  )
}
const AppContainer = styled(TamediaStyles)`${GlobalVariables}
  font-family: var(--font-sans-serif);
  font-size: 1.8rem;
  line-height: 3rem;
  margin-top: 50px;
`

const DateLabel = styled.div`
  font-size: 0.85em;
  margin-bottom: 1em;
`

const Container = styled(animated.div)`
  width: 100%;
  max-width: 600px;
  position: relative;
  margin-left: 300px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    margin-left: 0px;
  }
`
const Svg = styled.svg`
  will-change: transform;
`

const Path = styled(animated.path)`
  stroke: ${({ theme }) => `rgb(${theme.mainColor})`};
  fill: none;
  stroke-width: 90;
`

const SecondaryPath = styled(Path)`
  stroke: ${({ theme }) => `rgb(${theme.secondaryColor})`};
`

const RelatedLinks = styled.div`
  font-family: var(--font-sans-serif);
  font-size: 0.85em;
`

const Link = styled.a<{ secondary?: boolean }>`
  color: ${({ theme }) => theme.textColor};
  text-decoration: underline;
  padding: 0.2rem 0 0;
  background-image: ${({ theme, secondary }) =>
    `linear-gradient(to right,var(--link-background-color) 0,var(--link-background-color) 100%)`};
  background-size: 100% 20%;
  background-position-y: 17px;
  background-repeat: repeat-x;
  background-position: bottom;
`

const List = styled.ul`
  padding-left: 20px;
  list-style: none;
`

const ListItem = styled.li`
  margin-bottom: 0.5em;

  &:before {
    content: '-';
    color: ${({ theme }) => theme.textColor};
    display: inline-block;
    width: 1em;
    margin-left: -1em;
  }
`

const Legend = styled.div`
  font-weight: bold;
  max-width: 900px;
  margin: 0 auto;
  width: 100%;
  padding: 15px;
  box-sizing: border-box;
  position: fixed;
  left: 50%;
  top: 15px;
  transform: translateX(-50%);

  ${({ theme }) => theme.breakpoints.down('md')} {
    top: 60px;
  }
`

const LegendItem = styled.div`
background: ${({ theme }) => theme.backgroundColor};
display: inline-block;
padding-right: 0.5em;

`

const Pattern = styled.div<{ maskUrl: any }>`
  background: ${({ theme }) => `rgb(${theme.secondaryColor})`};
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  mask: url(${({ maskUrl }) => maskUrl});
  mask-size: cover;
  mask-repeat: no-repeat;
`

const GhostPath = styled(Path)`
  stroke: ${({ theme }) => theme.greyLight};
`

const RangePath = styled(Path)`
  stroke-width: 94;
  // stroke: url(#diagonalHatch);
  stroke: ${({ theme }) => `rgb(${theme.secondaryColor})`};
`

const Texts = styled.g`
  path {
    fill: ${({ theme }) => theme.backgroundColorInverted};
  }
`

const PathPattern = styled.path`
  stroke-width: 1;
  stroke: ${({ theme }) => `rgb(${theme.mainColor})`};
`

const TodayLine = styled.line`
  stroke: transparent;
  stroke-width: 3;
  stroke-dasharray: 0 5;
  stroke-linecap: round;
  marker-start: url(#arrowRight);
  marker-end: url(#arrowLeft);
`

const MonthLine = styled.line`
  stroke: ${({ theme }) => theme.backgroundColor};
  stroke-width: 2;
`

const Event = styled.circle<{ active?: boolean }>`
  // stroke: ${({ theme }) => `rgb(${theme.mainColor})`};
`

const Scrollyrtelling = styled.div`
  margin: -100px 0 -50px 0;
`

const ScrollytellingContainer = styled.div`
  position: relative;
  width: 100%;
  margin-top: calc(-100vh + 100px);

  ${({ theme }) => theme.breakpoints.down('md')} {
    margin-top: -50vh;
  }
`

const StepContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  padding-bottom: 75vh;

  ${({ theme }) => theme.breakpoints.down('md')} {
    padding-bottom: 100vh;
  }
`

const StepContent = styled.div`
  max-width: 300px;
  padding: 15px;
  background: ${({ theme }) => theme.backgroundColor};
  line-height: 1.5;
  font-family: var(--font-serif);
  font-size: 1.1em;
  -webkit-font-smoothing: antialiased;
  position: relative;

  // ${({ theme }) => theme.breakpoints.up('xxxl')} {
  //   transform: translateX(-100%);
  // }

  p:first-child {
    margin-top: 0;
  }
`

const StepText = styled.div``

const StickyChart = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  height: 100vh;
  pointer-events: none;
`

export default MainComponent
